import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import { useT } from "../../i18n/I18nProvider";
import { getThreads, type MailThread } from "../../lib/mail";
import { fmtStamp } from "../ui/format";
import { Pop, useClosePops } from "./Pop";
import { useSignals } from "./SignalsProvider";

/** Loaded when the panel opens rather than on every poll: three unread subject
 *  lines are only worth a request at the moment somebody asks to see them. */
function UnreadList({ onGo }: { onGo: (to: string) => void }) {
  const t = useT();
  const [rows, setRows] = useState<MailThread[] | null>(null);

  useEffect(() => {
    let alive = true;
    getThreads({ unread: true, pageSize: 4 })
      .then((r) => {
        if (alive) setRows(r.rows);
      })
      .catch(() => {
        if (alive) setRows([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (rows === null) return <div className="crm-pal-empty">{t("common.loading")}</div>;
  if (!rows.length) return <div className="crm-pal-empty">{t("mailMenu.empty")}</div>;

  return (
    <>
      {rows.map((thread) => (
        <button key={thread.id} type="button" className="crm-mi" onClick={() => onGo(`/email/${thread.id}`)}>
          <span className="crm-unread-dot" />
          <span className="min-w-0 flex-1">
            <span className="crm-trunc block">{thread.matchedCompanyName ?? thread.client?.name ?? thread.subject}</span>
            <span className="crm-mi-sub crm-trunc block">{thread.subject}</span>
          </span>
          <span className="crm-mut shrink-0 text-[11px]">{fmtStamp(thread.lastMessageAt)}</span>
        </button>
      ))}
    </>
  );
}

/** The inbox peek in the header: how many are unread, the newest few, and one
 *  way through to the mailbox itself. */
export function MailMenu() {
  const t = useT();
  const navigate = useNavigate();
  const close = useClosePops();
  const { badges } = useSignals();
  const unread = badges.mailUnread ?? 0;

  const go = (to: string) => {
    close();
    navigate(to);
  };

  return (
    <Pop
      wide
      trigger={(open) => (
        <button
          type="button"
          className="crm-rb"
          title={t("nav.email")}
          aria-label={t("nav.email")}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Mail className="size-[18px]" strokeWidth={1.7} />
          {unread ? <span className="crm-pip">{unread}</span> : null}
        </button>
      )}
    >
      <div className="crm-menu-head">
        <div className="crm-menu-t">{t("nav.email")}</div>
        <div className="crm-menu-s">{t("mailMenu.unread", { n: unread })}</div>
      </div>
      <UnreadList onGo={go} />
      <div className="crm-menu-sep" />
      <div className="crm-menu-foot">
        <button type="button" className="crm-btn crm-btn-sm w-full justify-center" onClick={() => go("/email")}>
          {t("mailMenu.open")}
        </button>
      </div>
    </Pop>
  );
}
